import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { AdminOSImage, OSCategory, OSType } from '@/types/admin'

interface Filters {
  category: OSCategory | 'All'
  osType: OSType | 'All'
  status: 'Active' | 'Inactive' | 'All'
  search: string
}

interface AdminOSImageStore {
  // State
  images: AdminOSImage[]
  selectedIds: Set<string>
  filters: Filters

  // Actions
  setImages: (images: AdminOSImage[]) => void
  toggleSelection: (id: string) => void
  toggleAllSelection: (ids: string[]) => void
  clearSelection: () => void
  updateFilters: (filters: Partial<Filters>) => void
  resetFilters: () => void
}

const initialFilters: Filters = {
  category: 'All',
  osType: 'All',
  status: 'All',
  search: '',
}

export const useAdminOSImageStore = create<AdminOSImageStore>()(
  devtools(
    (set) => ({
      // Initial state
      images: [],
      selectedIds: new Set<string>(),
      filters: initialFilters,

      // Set images list
      setImages: (images) => set({ images }),

      // Toggle single selection
      toggleSelection: (id) =>
        set((state) => {
          const newSelectedIds = new Set(state.selectedIds)
          if (newSelectedIds.has(id)) {
            newSelectedIds.delete(id)
          } else {
            newSelectedIds.add(id)
          }
          return { selectedIds: newSelectedIds }
        }),

      // Toggle all selection
      toggleAllSelection: (ids) =>
        set((state) => {
          const allSelected = ids.every((id) => state.selectedIds.has(id))
          if (allSelected) {
            // Deselect all
            return { selectedIds: new Set<string>() }
          } else {
            // Select all
            return { selectedIds: new Set(ids) }
          }
        }),

      // Clear all selections
      clearSelection: () => set({ selectedIds: new Set<string>() }),

      // Update filters
      updateFilters: (newFilters) =>
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        })),

      // Reset filters to defaults
      resetFilters: () => set({ filters: initialFilters }),
    }),
    { name: 'AdminOSImageStore' }
  )
)
